import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Check, Sparkles, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { EXPERT_IMAGES } from '@/data/experts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExpertSelection = () => {
  const [experts, setExperts] = useState({});
  const [selectedExperts, setSelectedExperts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const topic = location.state?.topic;

  useEffect(() => {
    if (!topic) {
      navigate('/');
      return;
    }

    const fetchExperts = async () => {
      try {
        const response = await axios.get(`${API}/experts`);
        setExperts(response.data.experts);
      } catch (error) {
        toast.error('Failed to load experts');
      } finally {
        setLoading(false);
      }
    };

    fetchExperts();
  }, [topic, navigate]);

  const toggleExpert = (expertId) => {
    setError(null);
    setSelectedExperts(prev => {
      if (prev.includes(expertId)) {
        return prev.filter(id => id !== expertId);
      }
      if (prev.length >= 3) {
        toast.error('Maximum 3 experts allowed');
        return prev;
      }
      return [...prev, expertId];
    });
  };

  const handleGenerate = async () => {
    if (selectedExperts.length === 0) {
      toast.error('Please select at least one expert');
      return;
    }

    setGenerating(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API}/podcasts/generate`,
        { topic, expert_ids: selectedExperts },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const podcast = response.data.podcast;
      
      if (podcast.status === 'failed') {
        setError('Script generation failed. Please try again.');
        setGenerating(false);
        return;
      }
      
      navigate(`/loading/${podcast.podcast_id}`, { 
        state: { podcast, experts: selectedExperts } 
      });
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to generate podcast. Please try again.';
      setError(message);
      toast.error(message);
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0A0A0B' }}>
        <div className="w-12 h-12 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-28" style={{ backgroundColor: '#0A0A0B' }}>
      {/* Header */}
      <header className="sticky top-0 z-20 border-b px-4 sm:px-6 py-3 sm:py-4" style={{ borderColor: 'rgba(255,255,255,0.06)', backgroundColor: '#0A0A0B' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-white hover:bg-white/5 -ml-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Back</span>
          </Button>

          <div className="text-center flex-1 mx-4">
            <p className="text-[10px] sm:text-xs tracking-widest uppercase" style={{ color: '#64748B' }}>
              Topic
            </p>
            <h2 
              className="text-sm sm:text-xl font-medium truncate"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: '#F8FAFC' }}
              data-testid="selected-topic"
            >
              {topic}
            </h2>
          </div>

          <div className="w-10 sm:w-20" />
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
        <div className="text-center mb-6 sm:mb-10">
          <h1 
            className="text-2xl sm:text-4xl font-medium tracking-tight mb-2 sm:mb-3"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: '#F8FAFC' }}
          >
            Choose Your Experts
          </h1>
          <p className="text-sm sm:text-base" style={{ color: '#94A3B8' }}>
            Select 1-3 experts to join the debate
          </p>
        </div>

        {/* Expert Grid - Mobile optimized */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-8">
          {Object.entries(experts).map(([id, expert], index) => {
            const isSelected = selectedExperts.includes(id);
            
            return (
              <motion.div
                key={id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
              >
                <button
                  onClick={() => toggleExpert(id)}
                  className={`expert-card relative w-full aspect-[16/10] sm:aspect-[4/3] rounded-xl overflow-hidden border transition-all ${
                    isSelected 
                      ? 'border-purple-500 ring-2 ring-purple-500/30' 
                      : 'border-white/10 hover:border-purple-500/50'
                  }`}
                  style={{ backgroundColor: '#141417' }}
                  data-testid={`expert-${id}`}
                >
                  {/* Image */}
                  <div className="absolute inset-0">
                    <img
                      src={EXPERT_IMAGES[id]}
                      alt={expert.name}
                      className="w-full h-full object-cover object-top"
                      style={{ 
                        filter: isSelected ? 'grayscale(0%) brightness(1)' : 'grayscale(70%)'
                      }}
                    />
                    <div 
                      className="absolute inset-0"
                      style={{
                        background: isSelected 
                          ? 'linear-gradient(to top, rgba(109, 40, 217, 0.9) 0%, rgba(109, 40, 217, 0.3) 50%, transparent 100%)'
                          : 'linear-gradient(to top, rgba(10, 10, 11, 0.95) 0%, rgba(10, 10, 11, 0.6) 60%, transparent 100%)'
                      }}
                    />
                  </div>

                  {/* Content */}
                  <div className="absolute inset-0 flex flex-col justify-end p-3 sm:p-4">
                    <h3 
                      className="text-base sm:text-lg font-medium mb-0.5 sm:mb-1"
                      style={{ fontFamily: "'Cormorant Garamond', serif", color: '#F8FAFC' }}
                    >
                      {expert.name}
                    </h3>
                    {expert.title && (
                      <p className="text-[10px] sm:text-xs mb-1" style={{ color: '#6D28D9' }}>
                        {expert.title}
                      </p>
                    )}
                    <p className="text-[10px] sm:text-xs line-clamp-2" style={{ color: isSelected ? '#E2E8F0' : '#94A3B8' }}>
                      {expert.personality?.substring(0, 80)}...
                    </p>
                  </div>

                  {/* Selection Indicator */}
                  {isSelected && (
                    <div 
                      className="absolute top-2 right-2 sm:top-3 sm:right-3 w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: '#6D28D9' }}
                    >
                      <Check className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                    </div>
                  )}
                </button>
              </motion.div>
            );
          })}
        </div>
      </main>

      {/* Fixed Bottom Generate Button */}
      <div 
        className="fixed bottom-0 left-0 right-0 p-4 border-t z-30"
        style={{ 
          backgroundColor: 'rgba(10, 10, 11, 0.95)',
          backdropFilter: 'blur(20px)',
          borderColor: 'rgba(255,255,255,0.06)'
        }}
      >
        <div className="max-w-7xl mx-auto">
          {/* Error Message */}
          {error && (
            <div 
              className="flex items-center gap-2 mb-3 p-3 rounded-lg text-sm"
              style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#FCA5A5' }}
            >
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          
          <Button
            onClick={handleGenerate}
            disabled={selectedExperts.length === 0 || generating}
            className="w-full h-12 sm:h-14 bg-purple-600 hover:bg-purple-500 text-white font-medium rounded-xl sm:rounded-full glow-purple disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
            data-testid="generate-podcast-btn"
          >
            {generating ? (
              <>
                <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2 sm:mr-3" />
                Generating Script...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3" />
                Generate Podcast ({selectedExperts.length} expert{selectedExperts.length !== 1 ? 's' : ''})
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExpertSelection;
